/**
 * features/finance/PastelChanges.tsx
 * ------------------------------------------------------------------
 * FINANCE — Pastel Check, second tab: "Changes made to match Pastel".
 *
 * Pastel is the record (the school, 18 Oct 2026). A portal payment with no
 * Pastel receipt is reversed and an invoice raised by hand that Pastel
 * never charged is voided — first in bulk (seed/align-to-pastel.ts), then
 * from the cards. Whoever made the entry may know it was right, so every
 * change is listed here with who made the original entry and when, and an
 * Undo that puts it back through lib reconFixWrite.undoReconChange.
 * HIGH-RISK-CHANGES.md §13.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { History, Loader2, RotateCcw, Search } from 'lucide-react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { SoftCard, glassFieldClass } from '@/features/finance/ui';
import { RECON_CHANGES_COLLECTION, describeChange, type ReconChange } from '@/lib/reconChanges';
import { undoReconChange, type ReconActor } from '@/features/finance/reconFixWrite';
import type { Invoice, Payment } from '@/lib/financeCore';

const when = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function PastelChanges({ payments, invoices, actor }: { payments: Payment[]; invoices: Invoice[]; actor: ReconActor }) {
  const [changes, setChanges] = useState<ReconChange[] | null>(null);
  const [error, setError] = useState('');
  const [show, setShow] = useState<'applied' | 'undone' | 'all'>('applied');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, RECON_CHANGES_COLLECTION), orderBy('at', 'desc')),
    (snap) => setChanges(snap.docs.map((d) => ({ ...(d.data() as ReconChange), id: d.id }))),
    (e) => { setError(e.message); setChanges([]); },
  ), []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (changes ?? [])
      .filter((c) => show === 'all' || c.status === show)
      .filter((c) => !q || `${c.studentName} ${c.pastelCode} ${c.receiptNo} ${c.invoiceNo} ${c.originalBy}`.toLowerCase().includes(q));
  }, [changes, show, search]);
  const count = (s: 'applied' | 'undone') => (changes ?? []).filter((c) => c.status === s).length;

  async function undo(c: ReconChange) {
    setBusy(c.id);
    setError('');
    try {
      await undoReconChange(c, { payments, invoices }, actor);
      setConfirming(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <SoftCard pad="p-0" hover={false}>
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-100 flex items-center gap-2"><History size={15} /> Changes made to match Pastel</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Pastel is the record: a portal payment with no Pastel receipt reversed, an invoice raised by hand that Pastel
          never charged voided, a Pastel account linked to a pupil or added to the portal. If one was wrong, <b>Undo</b>
          puts it back — the payment written again, the invoice restored, or the Pastel code taken off the pupil (and an
          opening balance added with them voided).
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {(['applied', 'undone', 'all'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setShow(k)}
              aria-pressed={show === k}
              className={`text-xs font-medium px-2.5 py-1 rounded-full border ${show === k
                ? 'bg-[var(--accent-strong)] text-white border-transparent'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40'}`}
            >
              {k === 'applied' ? `In force (${count('applied')})` : k === 'undone' ? `Undone (${count('undone')})` : `All (${changes?.length ?? 0})`}
            </button>
          ))}
          <div className="relative ml-auto">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pupil, receipt, invoice or person" className={glassFieldClass('py-1 pl-8 w-60')} />
          </div>
        </div>
        {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
      </div>

      {changes === null ? (
        <p className="px-4 py-6 text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 px-4 font-medium">Pupil</th>
                <th className="py-2 px-4 font-medium">What was done</th>
                <th className="py-2 px-4 font-medium">The portal entry</th>
                <th className="py-2 px-4 font-medium">Done</th>
                <th className="py-2 px-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {shown.map((c) => (
                <tr key={c.id} className={c.status === 'undone' ? 'opacity-60' : ''}>
                  <td className="py-2 px-4">
                    <a href={`#/studentProfile/${c.studentId}`} className="text-gray-800 dark:text-gray-100 hover:underline">{c.studentName ?? c.studentId}</a>
                    {c.pastelCode && <span className="block font-mono text-[11px] text-gray-400">{c.pastelCode}</span>}
                  </td>
                  <td className="py-2 px-4 text-gray-800 dark:text-gray-100 tabular-nums">
                    {describeChange(c)}
                    {c.kind === 'reversePayment' && c.invoiceNo && <span className="block text-[11px] text-gray-400">against {c.invoiceNo}</span>}
                  </td>
                  <td className="py-2 px-4 text-xs text-gray-600 dark:text-gray-300">
                    {c.kind === 'addPupil' || c.kind === 'linkPupil'
                      ? 'In Pastel, not in the portal'
                      : <>{c.kind === 'reversePayment' ? 'Recorded' : 'Raised'}{c.originalBy ? <> by <b>{c.originalBy}</b></> : ''} · {when(c.originalAt)}</>}
                    <span className="block text-[11px] text-gray-400">{c.reason}</span>
                  </td>
                  <td className="py-2 px-4 text-xs text-gray-500">
                    {when(c.at)} · {c.byName}
                    {c.status === 'undone' && <span className="block text-[11px] text-amber-700 dark:text-amber-300">Undone {when(c.undoneAt)} by {c.undoneByName}</span>}
                  </td>
                  <td className="py-2 px-4 text-right whitespace-nowrap">
                    {c.status === 'applied' && (confirming === c.id ? (
                      <span className="inline-flex gap-1.5">
                        <button
                          onClick={() => undo(c)}
                          disabled={busy === c.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-[var(--accent-strong)] text-white disabled:opacity-50"
                        >
                          {busy === c.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Put it back
                        </button>
                        <button onClick={() => setConfirming(null)} className="px-2 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirming(c.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <RotateCcw size={12} /> Undo
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-sm text-gray-400">Nothing here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </SoftCard>
  );
}
